import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import ContributionMapping from './ContributionMapping';
import ContributionsFileUpload from './ContributionsFileUpload';
import ContributionMappingTable from './ContributionMappingTable';
import { getColumnMapping } from './ContributionMapping/worker';
import { parsingRows } from './ContributionMappingTable/worker';
import { InvestmentColumnKeys } from './utils';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { getPortfolioExists, PortfolioExistsResponse } from './api';
import { useClient } from 'shared/apollo';

const StyledDiv = styled.div`
  .heading-page {
    background-color: ${COLORS.grayLight};
    padding: ${SPACERS.medium};
    margin-bottom: ${SPACERS.extraLarge};
  }
  .file-upload-content {
  }
`;

interface PortfolioValidity {
  [donorEmail: string]:
    | {
        [portfolioName: string]: boolean | undefined;
      }
    | undefined;
}

const Contributions: React.FC = () => {
  const client = useClient();
  const [
    missingPortfolioRequestState,
    makeMissingPortfolioRequest,
  ] = useGraphQLRequest<PortfolioExistsResponse>();
  const [missingPortfolios, setMissingPortfolios] = useState<{
    [id: string]: boolean | undefined;
  }>({});
  const [missingDonorEmails, setMissingDonorEmails] = useState<{
    [id: string]: boolean | undefined;
  }>({});
  const [file, setFile] = useState<File | undefined>(undefined);
  const [steps, setSteps] = useState<1 | 2 | 3>(file ? 2 : 1);
  const [data, setData] = useState<string[][] | undefined>(undefined);
  const [columns, setColumns] = useState<
    ReturnType<typeof getColumnMapping> | undefined
  >(undefined);
  const [finalData, setFinalData] = useState<
    ReturnType<typeof parsingRows> | undefined
  >(undefined);

  useEffect(() => {
    document.body.style.backgroundColor = COLORS.whiteBase;
    return () => {
      document.body.style.backgroundColor = COLORS.whiteLight;
    };
  }, []);

  useEffect(() => {
    if (missingPortfolioRequestState.type !== 'REQUEST_SUCCESS') return;
    setMissingDonorEmails(s => {
      const newState = { ...s };
      finalData?.portfolioWithEmail.forEach(i => {
        newState[i.donorEmail] = false;
      });
      for (const i of missingPortfolioRequestState.data.bulkPortfolioExist
        .missingEmails) {
        newState[i] = true;
      }
      return newState;
    });
    setMissingPortfolios(s => {
      const newState = { ...s };
      finalData?.portfolioWithEmail.forEach(i => {
        newState[i.portfolioName] = false;
      });
      for (const i of missingPortfolioRequestState.data.bulkPortfolioExist
        .missingPortfolios) {
        newState[i] = true;
      }
      return newState;
    });
  }, [missingPortfolioRequestState]);

  useEffect(() => {
    if (finalData && client) {
      const findPortfolioAndDonorEmails = finalData.portfolioWithEmail.filter(
        x =>
          Boolean(
            missingDonorEmails[x.donorEmail] === undefined ||
              missingPortfolios[x.portfolioName] === undefined,
          ),
      );

      if (findPortfolioAndDonorEmails.length === 0) return;
      makeMissingPortfolioRequest(
        getPortfolioExists(client, findPortfolioAndDonorEmails),
      );
    }
  }, [finalData?.portfolioWithEmail]);

  const handleDeleteRow = useCallback(
    (rowIndex: number) => {
      setData(d => {
        if (!d) return d;
        const newState = [...d];
        newState.splice(rowIndex, 1);
        return newState;
      });
      setFinalData(f => {
        if (!f) return f;
        const newState = { ...f };
        if (!newState.result[rowIndex]) return f;
        newState.result[rowIndex] = undefined;
        delete newState.failedRowsIndexes[rowIndex];
        return newState;
      });
    },
    [setData, setFinalData],
  );

  const handleUpdateRow = useCallback(
    (
      rowIndex: number,
      columnIndex: number,
      systemKey: InvestmentColumnKeys,
      value: string,
    ) => {
      setData(d => {
        if (!d) return d;
        const newState = [...d];
        newState[rowIndex][columnIndex] = value;
        return newState;
      });
      setFinalData(f => {
        if (!f) return f;
        const newState = { ...f };
        if (!newState.result[rowIndex]) return f;
        newState.result[rowIndex]![systemKey] = value;
        const rowErrors = newState.failedRowsIndexes[rowIndex];
        if (rowErrors && rowErrors !== true) {
          rowErrors[columnIndex + 1] = undefined;
          if (Object.values(rowErrors).every(v => v === undefined)) {
            delete newState.failedRowsIndexes[rowIndex];
          }
        }
        return newState;
      });
    },
    [setData, setFinalData],
  );

  const moveToFileStep = useCallback(() => {
    setSteps(1);
  }, [setSteps]);
  const moveToMappingStep = useCallback(() => {
    setSteps(2);
  }, [setSteps]);
  const moveToTableStep = useCallback(() => {
    setSteps(3);
  }, [setSteps]);

  useEffect(() => {
    if (!data) return;
    const worker = new Worker(
      new URL('./ContributionMapping/worker.ts', import.meta.url),
    );

    worker.onmessage = (
      event: MessageEvent<ReturnType<typeof getColumnMapping> | undefined>,
    ) => {
      setColumns(event.data);
    };

    worker.postMessage(data);

    return () => {
      worker.terminate();
    };
  }, [data]);

  useEffect(() => {
    if (!columns) return;
    const worker = new Worker(
      new URL('./ContributionMappingTable/worker.ts', import.meta.url),
    );
    worker.onmessage = (
      event: MessageEvent<ReturnType<typeof parsingRows> | undefined>,
    ) => {
      setFinalData(event.data);
    };

    worker.postMessage({
      data,
      columns,
      isLoading:
        missingPortfolioRequestState.type === 'REQUEST_INIT' ||
        missingPortfolioRequestState.type === 'REQUEST_START',
      missingPortfolios,
      missingDonorEmails,
    });

    return () => {
      worker.terminate();
    };
  }, [columns, missingPortfolios, missingDonorEmails]);

  useEffect(() => {
    setSteps(s => {
      const showMappingStep = s === 1 && file;
      if (showMappingStep) {
        const reader = new FileReader();
        reader.onload = () => {
          const csvData = reader.result as string;
          const rows = csvData.split('\n');
          const data = rows.map(row => row.split(','));
          setData(data);
        };
        reader.readAsText(file);
        return 2;
      }
      return s;
    });
  }, [file, setSteps, setData]);

  return (
    <StyledDiv>
      <TrueImpactText variant="heading-page" className="heading-page">
        Bulk donation upload
      </TrueImpactText>
      {steps === 1 ? (
        <ContributionsFileUpload
          file={file}
          setFile={setFile}
          moveToMappingStep={moveToMappingStep}
        />
      ) : steps === 2 ? (
        <ContributionMapping
          data={data}
          columns={columns}
          moveToFileStep={moveToFileStep}
          moveToTableStep={moveToTableStep}
          moveToMappingStep={moveToMappingStep}
        />
      ) : (
        <ContributionMappingTable
          columns={columns}
          finalData={finalData}
          isLoadingValidation={
            missingPortfolioRequestState.type === 'REQUEST_INIT' ||
            missingPortfolioRequestState.type === 'REQUEST_START'
          }
          data={finalData?.dataWithNumber}
          handleDeleteRow={handleDeleteRow}
          handleUpdateRow={handleUpdateRow}
          moveToFileStep={moveToFileStep}
          moveToTableStep={moveToTableStep}
          moveToMappingStep={moveToMappingStep}
        />
      )}
    </StyledDiv>
  );
};

export default Contributions;
